// What sits behind two of the three numbers on Home (item #650).
// Complaints are counted, never listed as work (Ben, 2026-09-30) — tapping the
// number shows them, read-only, and a tap opens the building.
// Starts: the buildings building_lifecycle() calls starting soon whose planned
// date is this week (Kansas City) — the same rule get_home_tiles() counts.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../shared/supabase';
import { errorMessage } from '../../shared/errors';
import { overlayClickClose } from '../../shared/overlay-close';
import type { WorkScope } from '../../shared/scope';
import { kcDay } from './home-work';

export type TileKind = 'starts' | 'complaints';

interface Line {
  facilityId: string;
  name: string;
  detail: string;
  right: string;
}

function weekBounds(): [string, string] {
  const today = new Date(`${kcDay()}T12:00:00`);
  const dow = (today.getDay() + 6) % 7; // Monday = 0
  const mon = new Date(today);
  mon.setDate(today.getDate() - dow);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return [iso(mon), iso(sun)];
}

export function TileSheet({
  kind,
  scope,
  facilityIds,
  onClose,
}: {
  kind: TileKind;
  scope: WorkScope;
  facilityIds: string[] | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [lines, setLines] = useState<Line[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (kind === 'complaints') {
          let q = supabase
            .from('facility_feedback')
            .select('facility_id, sentiment, category, feedback_date, description, facilities ( facility_name )')
            .in('status', ['Open', 'In_Progress'])
            .in('sentiment', ['Negative', 'Critical'])
            .order('feedback_date', { ascending: false });
          if (scope !== 'all' && facilityIds) q = q.in('facility_id', facilityIds.length ? facilityIds : ['__none__']);
          const { data, error: e } = await q;
          if (e) throw e;
          if (cancelled) return;
          setLines(
            ((data ?? []) as unknown as {
              facility_id: string;
              sentiment: string;
              category: string | null;
              feedback_date: string | null;
              description: string | null;
              facilities: { facility_name: string | null } | null;
            }[]).map((r) => ({
              facilityId: r.facility_id,
              name: r.facilities?.facility_name ?? r.facility_id,
              detail: [r.sentiment, r.category?.replace(/_/g, ' '), (r.description ?? '').slice(0, 90)].filter(Boolean).join(' · '),
              right: r.feedback_date ?? '',
            })),
          );
        } else {
          const [mon, sun] = weekBounds();
          let q = supabase
            .from('v_account_management_cards')
            .select('facility_id, facility_name, planned_start_date, lifecycle')
            .eq('lifecycle', 'starting_soon')
            .gte('planned_start_date', mon)
            .lte('planned_start_date', sun)
            .order('planned_start_date');
          if (scope !== 'all' && facilityIds) q = q.in('facility_id', facilityIds.length ? facilityIds : ['__none__']);
          const { data, error: e } = await q;
          if (e) throw e;
          if (cancelled) return;
          setLines(
            ((data ?? []) as { facility_id: string; facility_name: string | null; planned_start_date: string | null }[]).map((r) => ({
              facilityId: r.facility_id,
              name: r.facility_name ?? r.facility_id,
              detail: 'Starting soon',
              right: r.planned_start_date ?? '',
            })),
          );
        }
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Could not load this list.'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, scope, facilityIds]);

  const title = kind === 'complaints' ? 'Open complaints — counted, not listed as work' : 'Starting this week';
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" aria-label="Close" className="absolute inset-0 bg-tcs-scrim/60" {...overlayClickClose(onClose)} />
      <div className="relative max-h-[80%] w-full overflow-y-auto rounded-t-2xl bg-tcs-bg p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl md:max-w-md md:rounded-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-[15.5px] font-semibold text-tcs-text">{title}</h2>
          <button type="button" onClick={onClose} className="min-h-[44px] px-2 text-[13px] text-tcs-text-secondary">
            Close
          </button>
        </div>
        {error && <p className="mt-2 text-[13px] text-st-danger-fg">{error}</p>}
        {lines === null && !error && <p className="mt-3 text-[13px] text-tcs-text-muted">Loading…</p>}
        {lines && lines.length === 0 && <p className="mt-3 text-[13px] text-tcs-text-muted">None in this view.</p>}
        {lines && lines.length > 0 && (
          <div className="mt-2 overflow-hidden rounded-xl border border-tcs-border">
            {lines.map((l, i) => (
              <button
                key={`${l.facilityId}-${i}`}
                type="button"
                onClick={() => navigate(`/account-management/facilities/${l.facilityId}`)}
                className="flex min-h-[48px] w-full items-start justify-between gap-2 border-b border-tcs-border px-3 py-2 text-left last:border-b-0"
              >
                <span className="min-w-0">
                  <span className="block text-[13.5px] font-medium text-tcs-text">{l.name}</span>
                  <span className="block text-[12px] text-tcs-text-secondary">{l.detail}</span>
                </span>
                <span className="shrink-0 text-[12px] tabular-nums text-tcs-text-muted">{l.right}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
